// Onglet « Règles locales » de la fiche logement (F-090) : régime saisi et sourcé, démarches guidées, pièces.
import { useState, type FormEvent } from "react";
import { put, upload } from "@core/api";
import { date, dateTime, money } from "@core/format";
import { useGet, useInvalidate } from "@core/hooks";
import { useOrg } from "@core/org";
import { Button, Card, ErrorBox, Fil, FileDrop, FileLine, Input, KeyValues, Loading, MoneyInput, Notice, Select, Textarea, fieldError, useToast } from "@core/ui";
import { SourceLinks, TRI_OPTIONS, triLabel, type Source } from "./shared";

type Step = { title: string; text: string; state: "fait" | "actuel" | "a-venir"; links: Source[] };

type LocalRules = {
  recorded: boolean;
  commune: string;
  zone_tendue: string;
  encadrement: string;
  ref_rent_cents_m2: number | null;
  ref_rent_majore_cents_m2: number | null;
  ref_valid_from: string | null;
  ref_valid_until: string | null;
  permit_regime: string;
  procedure_status: string;
  procedure_status_label: string;
  filed_on: string | null;
  decision_on: string | null;
  procedure_reference: string;
  source_label: string;
  source_url: string;
  source_checked_on: string | null;
  notes: string;
  updated_at: string | null;
  computed: {
    surface_m2: number | null;
    furnished: boolean;
    max_base_rent_cents: number | null;
    max_rent_explanation: string;
    ref_expired: boolean;
    tacit_approval_on: string | null;
    authorization_valid_until: string | null;
  };
  warnings: string[];
  steps: Step[];
  documents: { id: string; title: string; filename: string; size_bytes: number; url: string; created_at: string }[];
  sources: Source[];
  coverage: string;
  address_line: string;
  can_edit: boolean;
};

const REGIMES = [
  { value: "inconnu", label: "Je ne sais pas encore" },
  { value: "aucun", label: "Aucune démarche" },
  { value: "declaration", label: "Déclaration de mise en location" },
  { value: "autorisation", label: "Autorisation préalable de mise en location" },
];

const STATUSES: Record<string, { value: string; label: string }[]> = {
  inconnu: [{ value: "a_verifier", label: "À vérifier" }],
  aucun: [{ value: "non_concerne", label: "Non concerné" }],
  declaration: [
    { value: "a_preparer", label: "À préparer" },
    { value: "deposee", label: "Déposée" },
    { value: "enregistree", label: "Enregistrée (récépissé reçu)" },
  ],
  autorisation: [
    { value: "a_preparer", label: "À préparer" },
    { value: "deposee", label: "Déposée, en attente de réponse" },
    { value: "accordee", label: "Accordée (décision reçue)" },
    { value: "accord_tacite", label: "Accord tacite (sans réponse dans le mois)" },
    { value: "refusee", label: "Refusée" },
    { value: "caduque", label: "Caduque" },
  ],
};

const DOC_KINDS = [
  { value: "recepisse_declaration", label: "Récépissé de déclaration" },
  { value: "accuse_depot", label: "Accusé de dépôt de la demande" },
  { value: "decision_autorisation", label: "Décision d’autorisation ou de refus" },
  { value: "arrete_loyers", label: "Arrêté fixant les loyers de référence" },
  { value: "autre", label: "Autre pièce" },
];

export function LocalRulesTab({ propertyId, canEdit }: { propertyId: string; canEdit: boolean }) {
  const org = useOrg();
  const q = useGet<LocalRules>(org.api(`/properties/${propertyId}/local-rules`));
  const [editing, setEditing] = useState(false);
  if (q.isLoading) return <Loading />;
  if (q.error || !q.data) return <ErrorBox error={q.error} />;
  const r = q.data;
  return (
    <div className="stack">
      <Notice tone="attention" title="Couverture">
        {r.coverage}
      </Notice>
      {r.warnings.map((w) => (
        <Notice key={w} tone="attention">
          {w}
        </Notice>
      ))}
      {!editing ? (
        <div className="grid-2">
          <Card title="Ce que vous avez constaté" actions={canEdit ? <Button small variant="secondaire" onClick={() => setEditing(true)}>{r.recorded ? "Modifier" : "Renseigner"}</Button> : null}>
            <KeyValues
              items={[
                ["Commune", r.commune || "—"],
                ["Zone tendue", triLabel(r.zone_tendue)],
                ["Encadrement des loyers", triLabel(r.encadrement)],
                ["Loyer de référence", r.ref_rent_cents_m2 ? `${money(r.ref_rent_cents_m2)} / m² / mois` : "—"],
                ["Loyer de référence majoré", r.ref_rent_majore_cents_m2 ? `${money(r.ref_rent_majore_cents_m2)} / m² / mois` : "—"],
                ["Valeurs en vigueur", r.ref_valid_from || r.ref_valid_until ? `${r.ref_valid_from ? `du ${date(r.ref_valid_from)} ` : ""}${r.ref_valid_until ? `au ${date(r.ref_valid_until)}` : ""}` : "—"],
                ["Démarche de mise en location", REGIMES.find((x) => x.value === r.permit_regime)?.label ?? r.permit_regime],
                ["État de la démarche", r.procedure_status_label],
                ["Dépôt", r.filed_on ? `le ${date(r.filed_on)}` : "—"],
                ["Décision reçue", r.decision_on ? `le ${date(r.decision_on)}` : "—"],
                ["Référence", r.procedure_reference || "—"],
              ]}
            />
            <p className="small">
              <strong>Source :</strong> {r.source_label || "non renseignée"}
              {r.source_url ? (
                <>
                  {" "}
                  ·{" "}
                  <a href={r.source_url} target="_blank" rel="noreferrer">
                    lien
                  </a>
                </>
              ) : null}
              {r.source_checked_on ? ` · consultée le ${date(r.source_checked_on)}` : ""}
              {r.updated_at ? <span className="pat-sub">Mis à jour le {dateTime(r.updated_at)}</span> : null}
            </p>
            {r.notes ? <p className="small">{r.notes}</p> : null}
          </Card>
          <div className="stack">
            <Card title="Loyer de base maximal">
              {r.computed.max_base_rent_cents !== null ? (
                <p>
                  <strong className="num">{money(r.computed.max_base_rent_cents)}</strong> par mois, hors charges et hors complément de loyer.
                </p>
              ) : null}
              <p className="small muted">{r.computed.max_rent_explanation}</p>
              {r.computed.authorization_valid_until ? (
                <p className="small">Autorisation utilisable pour une mise en location jusqu’au {date(r.computed.authorization_valid_until)}.</p>
              ) : null}
              {r.computed.tacit_approval_on && r.procedure_status === "deposee" ? (
                <p className="small">Sans réponse au {date(r.computed.tacit_approval_on)}, l’autorisation est considérée comme accordée : le dépôt seul n’est pas une autorisation.</p>
              ) : null}
            </Card>
            <Card title="Sources officielles">
              <SourceLinks sources={r.sources} />
            </Card>
          </div>
        </div>
      ) : (
        <LocalRulesForm propertyId={propertyId} r={r} onDone={() => setEditing(false)} />
      )}
      <Card title="Démarches">
        {r.steps.length ? <Fil steps={r.steps.map((s) => ({ title: s.title, text: <>{s.text} <SourceLinks sources={s.links} /></>, state: s.state }))} /> : <p className="muted">Aucune démarche à suivre d’après vos réponses.</p>}
      </Card>
      <Card title="Pièces de la démarche">
        {r.documents.length === 0 ? <p className="muted">Aucune pièce jointe (récépissé, décision, arrêté).</p> : null}
        {r.documents.map((d) => (
          <FileLine key={d.id} name={d.title || d.filename} size={d.size_bytes} href={d.url} right={<span className="small muted">{date(d.created_at)}</span>} />
        ))}
        {canEdit ? <UploadDoc propertyId={propertyId} /> : null}
      </Card>
    </div>
  );
}

function UploadDoc({ propertyId }: { propertyId: string }) {
  const org = useOrg();
  const toast = useToast();
  const invalidate = useInvalidate();
  const [kind, setKind] = useState("recepisse_declaration");
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  return (
    <div className="stack-sm">
      <ErrorBox error={error} />
      <div className="form-row">
        <Select label="Type de pièce" value={kind} options={DOC_KINDS} onChange={(e) => setKind(e.target.value)} />
      </div>
      <FileDrop
        busy={busy}
        onFiles={async (files) => {
          if (!files[0]) return;
          setBusy(true);
          setError(null);
          try {
            const form = new FormData();
            form.append("kind", kind);
            form.append("file", files[0]);
            await upload(org.api(`/properties/${propertyId}/local-rules/documents`), form);
            invalidate(org.api(`/properties/${propertyId}`));
            toast("Pièce ajoutée.");
          } catch (e) {
            setError(e);
          } finally {
            setBusy(false);
          }
        }}
      />
    </div>
  );
}

function LocalRulesForm({ propertyId, r, onDone }: { propertyId: string; r: LocalRules; onDone: () => void }) {
  const org = useOrg();
  const toast = useToast();
  const invalidate = useInvalidate();
  const [f, setF] = useState({
    commune: r.commune,
    zone_tendue: r.zone_tendue,
    encadrement: r.encadrement,
    ref_rent_cents_m2: r.ref_rent_cents_m2,
    ref_rent_majore_cents_m2: r.ref_rent_majore_cents_m2,
    ref_valid_from: r.ref_valid_from ?? "",
    ref_valid_until: r.ref_valid_until ?? "",
    permit_regime: r.permit_regime,
    procedure_status: r.procedure_status,
    filed_on: r.filed_on ?? "",
    decision_on: r.decision_on ?? "",
    procedure_reference: r.procedure_reference,
    source_label: r.source_label,
    source_url: r.source_url,
    source_checked_on: r.source_checked_on ?? "",
    notes: r.notes,
  });
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const set = <K extends keyof typeof f>(k: K, v: (typeof f)[K]) => setF((s) => ({ ...s, [k]: v }));
  const err = (k: string) => fieldError(error, k);
  const statuses = STATUSES[f.permit_regime] ?? STATUSES.inconnu;
  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await put(org.api(`/properties/${propertyId}/local-rules`), {
        ...f,
        procedure_status: statuses.some((s) => s.value === f.procedure_status) ? f.procedure_status : statuses[0].value,
        ref_valid_from: f.ref_valid_from || null,
        ref_valid_until: f.ref_valid_until || null,
        filed_on: f.filed_on || null,
        decision_on: f.decision_on || null,
        source_checked_on: f.source_checked_on || null,
        ref_rent_cents_m2: f.encadrement === "oui" ? f.ref_rent_cents_m2 : null,
        ref_rent_majore_cents_m2: f.encadrement === "oui" ? f.ref_rent_majore_cents_m2 : null,
      });
      invalidate(org.api("/properties"));
      toast("Règles locales enregistrées.");
      onDone();
    } catch (e2) {
      setError(e2);
    } finally {
      setBusy(false);
    }
  }
  return (
    <Card title="Renseigner les règles locales">
      <form className="stack" onSubmit={submit} noValidate>
        <p className="small muted">Répondez « Je ne sais pas » plutôt que de deviner : l’information restera signalée comme à vérifier. Toute réponse connue demande sa source et sa date.</p>
        <ErrorBox error={error && !(error as any).fields?.length ? error : null} />
        <Input label="Commune" value={f.commune} onChange={(e) => set("commune", e.target.value)} />
        <fieldset className="pat-fieldset">
          <legend>Zone tendue et encadrement</legend>
          <div className="form-row">
            <Select label="Le logement est-il en zone tendue ?" value={f.zone_tendue} options={TRI_OPTIONS} error={err("zone_tendue")} onChange={(e) => set("zone_tendue", e.target.value)} />
            <Select label="L’encadrement des loyers s’applique-t-il ?" value={f.encadrement} options={TRI_OPTIONS} error={err("encadrement")} onChange={(e) => set("encadrement", e.target.value)} />
          </div>
          {f.encadrement === "oui" ? (
            <>
              <div className="form-row">
                <MoneyInput label="Loyer de référence (€ / m² / mois)" cents={f.ref_rent_cents_m2} error={err("ref_rent_cents_m2")} onChange={(c) => set("ref_rent_cents_m2", c)} />
                <MoneyInput
                  label="Loyer de référence majoré (€ / m² / mois)"
                  cents={f.ref_rent_majore_cents_m2}
                  error={err("ref_rent_majore_cents_m2")}
                  help={r.computed.furnished ? "Valeur applicable aux logements meublés." : "Valeur applicable aux logements vides."}
                  onChange={(c) => set("ref_rent_majore_cents_m2", c)}
                />
              </div>
              <div className="form-row">
                <Input label="En vigueur à partir du" type="date" value={f.ref_valid_from} error={err("ref_valid_from")} onChange={(e) => set("ref_valid_from", e.target.value)} />
                <Input label="Jusqu’au" type="date" value={f.ref_valid_until} error={err("ref_valid_until")} onChange={(e) => set("ref_valid_until", e.target.value)} />
              </div>
            </>
          ) : null}
        </fieldset>
        <fieldset className="pat-fieldset">
          <legend>Démarche de mise en location</legend>
          <div className="form-row">
            <Select label="Régime" value={f.permit_regime} options={REGIMES} error={err("permit_regime")} onChange={(e) => set("permit_regime", e.target.value)} />
            <Select label="État" value={statuses.some((s) => s.value === f.procedure_status) ? f.procedure_status : statuses[0].value} options={statuses} error={err("procedure_status")} onChange={(e) => set("procedure_status", e.target.value)} />
          </div>
          {f.permit_regime === "declaration" || f.permit_regime === "autorisation" ? (
            <div className="form-row">
              <Input label="Date de dépôt" type="date" value={f.filed_on} error={err("filed_on")} onChange={(e) => set("filed_on", e.target.value)} />
              {f.permit_regime === "autorisation" ? <Input label="Date de la décision reçue" type="date" value={f.decision_on} error={err("decision_on")} onChange={(e) => set("decision_on", e.target.value)} /> : null}
              <Input label="Référence (récépissé, dossier)" value={f.procedure_reference} onChange={(e) => set("procedure_reference", e.target.value)} />
            </div>
          ) : null}
        </fieldset>
        <fieldset className="pat-fieldset">
          <legend>Source de ces informations</legend>
          <Input label="Source" placeholder="Ex. Mairie de Montpellier, service habitat" value={f.source_label} error={err("source_label")} onChange={(e) => set("source_label", e.target.value)} />
          <div className="form-row">
            <Input label="Lien (facultatif)" type="url" value={f.source_url} error={err("source_url")} onChange={(e) => set("source_url", e.target.value)} />
            <Input label="Consultée le" type="date" value={f.source_checked_on} error={err("source_checked_on")} onChange={(e) => set("source_checked_on", e.target.value)} />
          </div>
        </fieldset>
        <Textarea label="Notes" value={f.notes} onChange={(e) => set("notes", e.target.value)} />
        <div className="form-actions">
          <Button variant="discret" onClick={onDone}>
            Annuler
          </Button>
          <Button type="submit" busy={busy}>
            Enregistrer
          </Button>
        </div>
      </form>
    </Card>
  );
}
